import {
  Account,
  ImpersonationSelection,
  ImpersonationState,
  Persona,
  Tenant,
} from "../../types";
import {
  Avatar,
  Box,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import CheckIcon from "@mui/icons-material/Check";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import ClickableSection from "@features/impersonation-feature/components/Panel/ClickableSection";
import SearchPopover from "@features/impersonation-feature/components/Panel/SearchPopover";
import useGetTenant, {
  isOnboardedTenant,
} from "@features/impersonation-feature/hooks/useGetTenant";
import { useSelectorPopover } from "../../useSelectorPopover";
import { isSameOriginAccount } from "../../selectionIdentity";
import { TENANT_ID_UNDEFINED } from "@features/impersonation-feature/constants";
import { hasAccountIdentity } from "@features/impersonation-feature/accountIdentity";
import {
  createVirtualAdminPersona,
  getPersonaForAccount,
  getWorkingSelection,
  updateSelectionWithPersona,
} from "@features/impersonation-feature/selectionState";
import UserAvatar from "./UserAvatar";

interface AccountImpersonationSelectorProps {
  pending: ImpersonationSelection | null;
  working: ImpersonationSelection | null;
  origin?: ImpersonationSelection | null;
  setImpersonationState: React.Dispatch<
    React.SetStateAction<ImpersonationState>
  >;
}

const LISTBOX_ID = "impersonation-account-options";

// Visually hides content while keeping it available to assistive technology.
const srOnlySx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

type AccountOption = {
  account: Account;
  tenant: Tenant;
};

const getAccountOptionKey = (account: Account) =>
  `${account.id ?? ""}-${account.axNumber ?? ""}`;

const getPersonaLabel = (impersonatingUser: boolean, user: Persona | null) => {
  if (!impersonatingUser) {
    return "Virtual Admin";
  }
  if (!user) {
    return "Unknown User";
  }
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  const role = user.isAdmin ? "Admin" : user.isGuest ? "Guest" : null;
  return [name, role].filter(Boolean).join(" • ") || "Unknown User";
};

export default function AccountSelector({
  pending,
  working,
  origin,
  setImpersonationState,
}: AccountImpersonationSelectorProps) {
  const [searchString, setSearchString] = useState("");
  const {
    anchorEl,
    isOpen,
    searchInputRef,
    listRef,
    closeSelector,
    handleOpen,
    handleSummaryKeyDown,
    handleSearchKeyDown,
    handleOptionKeyDown,
  } = useSelectorPopover({ pending, setImpersonationState });

  const impersonatingUser =
    working?.user != null && working.user.type !== "account";

  const tenantId = working?.tenant?.id || TENANT_ID_UNDEFINED;
  const accountId = working?.account?.id || null;

  const isNotOnboardedMode =
    tenantId === TENANT_ID_UNDEFINED ||
    !isOnboardedTenant(working?.tenant?.onBoardingStatus ?? null);

  const { notOnboarded, isInitialLoading } = useGetTenant(searchString);

  const accountOptions: AccountOption[] = useMemo(() => {
    const sortAccounts = (a: AccountOption, b: AccountOption) => {
      // Sort by name first, then by axNumber
      const cmp = (a.account.name || "").localeCompare(b.account.name || "");
      if (cmp !== 0) return cmp;
      return (a.account.axNumber || "").localeCompare(b.account.axNumber || "");
    };

    const result = notOnboarded
      .flatMap((tenant) =>
        tenant.accounts.map((account) => ({
          account,
          tenant,
        })),
      )
      .sort(sortAccounts);

    const existingKeys = new Set<string>();
    const uniqueResult = result.filter((option) => {
      const key = getAccountOptionKey(option.account);
      if (existingKeys.has(key)) return false;
      existingKeys.add(key);
      return true;
    });

    if (working?.tenant?.accounts?.length) {
      const workingOption: AccountOption[] = working.tenant.accounts
        .filter((account) =>
          hasAccountIdentity(account.id, account.name, account.axNumber),
        )
        .map((account) => ({
          account,
          tenant: working.tenant as Tenant,
        }));

      const uniqueWorkingOptions = workingOption.filter((opt) => {
        const key = getAccountOptionKey(opt.account);
        if (existingKeys.has(key)) return false;
        existingKeys.add(key);
        return true;
      });
      return [...uniqueWorkingOptions, ...uniqueResult];
    }

    return uniqueResult;
  }, [notOnboarded, working?.tenant]);

  const axNumber = working?.account?.axNumber || null;
  const accountDisplay =
    [working?.account?.name, axNumber == null ? null : `CA: ${axNumber}`]
      .filter(Boolean)
      .join(" • ") || "Select a Customer Account";

  const handleSelectAccount = (option: AccountOption) => {
    setImpersonationState((prev) => {
      const baseSelection = getWorkingSelection(prev);

      // Validate required context before proceeding
      if (!baseSelection?.tenant) {
        return prev;
      }

      const nextSelection: ImpersonationSelection = {
        ...baseSelection,
        account: option.account,
        tenant: option.tenant,
        user: isSameOriginAccount(
          {
            ...baseSelection,
            tenant: option.tenant,
            account: option.account,
          },
          origin ?? null,
        )
          ? (origin?.user ?? null)
          : option.account.id === baseSelection.account?.id
            ? baseSelection.user
            : isOnboardedTenant(option.tenant.onBoardingStatus)
              ? (getPersonaForAccount(prev, option.account.id) ??
                createVirtualAdminPersona(option.account.id))
              : null,
      };

      return updateSelectionWithPersona(prev, nextSelection);
    });

    closeSelector();
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "fit-content" },
        maxWidth: {
          xs: "100%",
          sm: (theme) => `min(92vw, ${theme.breakpoints.values.md}px)`,
        },
        minWidth: 0,
      }}
    >
      <ClickableSection
        {...telemetryProps({
          name: "impersonation_context_selector",
          section: "impersonation_panel",
          label: "Customer Account Selector",
        })}
        onClick={handleOpen}
        onKeyDown={handleSummaryKeyDown}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-controls={LISTBOX_ID}
        aria-expanded={isOpen}
        aria-label={
          accountId != null
            ? `Customer account selector. Selected: ${accountDisplay}`
            : "Customer account selector. No account selected"
        }
        isActive={isOpen}
      >
        {impersonatingUser ? (
          <UserAvatar
            photoUrl={working?.user?.photo ?? undefined}
            isAdmin={working?.user?.isAdmin ?? false}
            initials={
              working?.user?.initials ?? (working?.user?.isAdmin ? "VA" : "U")
            }
          />
        ) : (
          <Avatar
            sx={{
              width: (theme) => theme.spacing(4),
              height: (theme) => theme.spacing(4),
              bgcolor: "action.selected",
              flexShrink: 0,
            }}
          >
            <AccountBoxIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Avatar>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "fontWeightBold",
              lineHeight: "body2.lineHeight",
              wordBreak: "break-word",
            }}
            noWrap={false}
          >
            {accountDisplay}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: "caption.lineHeight", display: "block" }}
          >
            {getPersonaLabel(impersonatingUser, working?.user ?? null)}
          </Typography>
        </Box>
      </ClickableSection>

      <SearchPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeSelector}
        onDebouncedSearchChange={(value) => {
          setSearchString(value);
        }}
        onSearchKeyDown={handleSearchKeyDown}
        searchPlaceholder="Search customer accounts..."
        searchInputRef={searchInputRef}
        searchInputName="impersonation-account-search"
        searchInputAriaLabel="Search customer accounts"
        isSearching={(state) => isInitialLoading || state.isDebouncePending}
        searchTelemetryProps={telemetryProps({
          name: "switch_accounts",
          section: "impersonation_panel",
          label: "Switch Accounts",
        })}
        listId={LISTBOX_ID}
        listAriaLabel="Customer account options"
        listRef={listRef}
        statusMessage={(state) => {
          const displayedAccounts = state.shouldHide
            ? []
            : (accountOptions ?? []);

          if (!tenantId) {
            return "Select a tenant first";
          }

          if (state.shouldHide) {
            return "Type at least 3 characters to search";
          }

          if (isInitialLoading || state.isDebouncePending) {
            return "Searching accounts…";
          }

          if (isNotOnboardedMode && displayedAccounts.length > 0) {
            return `${displayedAccounts.length} account${displayedAccounts.length === 1 ? "" : "s"} found (all customer accounts)`;
          }

          if (displayedAccounts.length === 0) {
            return "No accounts found";
          }

          return `${displayedAccounts.length} account${displayedAccounts.length === 1 ? "" : "s"} found`;
        }}
        isEmpty={(state) => {
          if (!tenantId) {
            return true;
          }

          const displayedAccounts = state.shouldHide
            ? []
            : (accountOptions ?? []);
          return displayedAccounts.length === 0;
        }}
        emptyMessage={(state) => {
          if (!tenantId) {
            return "Select a tenant first";
          }

          const displayedAccounts = state.shouldHide
            ? []
            : (accountOptions ?? []);

          if (state.shouldHide) {
            return "Type at least 3 characters";
          }

          if (isInitialLoading || state.isDebouncePending) {
            return "Searching accounts…";
          }

          if (displayedAccounts.length === 0) {
            return "No accounts found";
          }

          return "";
        }}
        statusSx={srOnlySx}
      >
        {(state) => {
          const displayedAccounts = state.shouldHide
            ? []
            : (accountOptions ?? []);

          return displayedAccounts.map((option, index) => {
            const isSelected = option.account.id === accountId;

            return (
              <ListItemButton
                key={getAccountOptionKey(option.account)}
                role="option"
                aria-selected={isSelected}
                selected={isSelected}
                onClick={() => handleSelectAccount(option)}
                onKeyDown={(e) =>
                  handleOptionKeyDown(e, index, displayedAccounts.length)
                }
                sx={{
                  gap: 1.25,
                  py: { xs: 1.25, sm: 1 },
                  px: { xs: 1.5, sm: 2 },
                }}
              >
                <Avatar
                  sx={{
                    width: (theme) => theme.spacing(3.5),
                    height: (theme) => theme.spacing(3.5),
                    bgcolor: "action.selected",
                    color: "text.secondary",
                    flexShrink: 0,
                  }}
                >
                  <AccountBoxIcon fontSize="small" />
                </Avatar>
                <ListItemText
                  primary={[
                    option.account.name,
                    option.account.axNumber
                      ? `CA: ${option.account.axNumber}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                  slotProps={{
                    primary: {
                      variant: "body2",
                      fontWeight: "fontWeightMedium",
                    },
                  }}
                />
                {isSelected ? (
                  <CheckIcon
                    fontSize="small"
                    sx={{ color: "primary.main", flexShrink: 0 }}
                  />
                ) : null}
              </ListItemButton>
            );
          });
        }}
      </SearchPopover>
    </Box>
  );
}
